import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import api from "./api";
import { requestFcmToken } from "./firebase";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("sf_token"));
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("sf_user")) || null;
    } catch {
      return null;
    }
  });


  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let registrationHandle;
    let errorHandle;

    const setupNativePush = async () => {
      try {
        registrationHandle = await PushNotifications.addListener("registration", ({ value }) => {
          console.log("[Native FCM] token:", value);
          localStorage.setItem("sf_native_fcm_token", value);

          const currentToken = localStorage.getItem("sf_token");
          if (currentToken) {
            api.post("/notifications/register-device", {
              token: value,
              platform: "android"
            }).then(() => {
              console.log("[Native FCM] token registered");
            }).catch((e) => {
              console.error("[Native FCM] register-device failed:", e);
            });
          }
        });

        errorHandle = await PushNotifications.addListener("registrationError", (error) => {
          console.error("[Native FCM] registration error:", error);
        });

        await PushNotifications.createChannel({
          id: "sabuj_important",
          name: "Important Orders & Payments",
          description: "Important order, cancellation, acceptance and payment notifications",
          importance: 5,
          visibility: 1,
          vibration: true,
          lights: true
        });

        const permission = await PushNotifications.requestPermissions();

        if (permission.receive === "granted") {
          await PushNotifications.register();
        } else {
          console.warn("[Native FCM] notification permission not granted");
        }
      } catch (e) {
        console.error("[Native FCM] setup failed:", e);
      }
    };

    setupNativePush();

    return () => {
      registrationHandle?.remove();
      errorHandle?.remove();
    };
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const checkForAppUpdate = async () => {
      try {
        const response = await fetch("/app-version.json?ts=" + Date.now(), {
          cache: "no-store"
        });
        if (!response.ok) return;

        const latest = await response.json();
        const currentVersion =
          window.SabujApkUpdater?.getVersionCode
            ? Number(window.SabujApkUpdater.getVersionCode())
            : 2;

        if (Number(latest.versionCode || 0) > currentVersion) {
          if (
            latest.apkUrl &&
            window.SabujApkUpdater?.downloadAndInstall
          ) {
            console.log(
              "[APK Update] New version available:",
              latest.versionName || latest.versionCode
            );

            window.SabujApkUpdater.downloadAndInstall(latest.apkUrl);
          }
        }
      } catch (e) {
        console.error("[APK Update] check failed:", e);
      }
    };

    const updateStatusHandler = (event) => {
      const detail = event.detail || {};
      if (detail.status === "success") {
        console.log("[APK Update] Download successful");
      }
    };

    window.addEventListener("sabuj-apk-update-status", updateStatusHandler);

    checkForAppUpdate();

    return () => {
      window.removeEventListener("sabuj-apk-update-status", updateStatusHandler);
    };
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !token) return;

    const nativeFcmToken = localStorage.getItem("sf_native_fcm_token");
    if (!nativeFcmToken) return;

    api.post("/notifications/register-device", {
      token: nativeFcmToken,
      platform: "android"
    }).then(() => {
      console.log("[Native FCM] token registered");
    }).catch((e) => {
      console.error("[Native FCM] register-device failed:", e);
    });
  }, [token]);

  const login = useCallback((newToken, newUser, role, deviceSession = null) => {
    const u = { ...newUser, role };
    localStorage.setItem("sf_token", newToken);
    localStorage.setItem("sf_user", JSON.stringify(u));
    localStorage.setItem("sf_role", role);

    if (role === "admin" && deviceSession) {
      localStorage.setItem("sf_device_session", deviceSession);
    }

    setToken(newToken);
    setUser(u);

    if (!Capacitor.isNativePlatform()) {

      requestFcmToken().then((fcmToken) => {
        console.log("[FCM] token:", fcmToken);
        if (fcmToken) {
          api.post("/notifications/register-device", {
            token: fcmToken,
            platform: "web"
          }).then(() => {
            console.log("[FCM] token registered");
          }).catch((e) => {
            console.error("[FCM] register-device failed:", e);
          });
        } else {
          console.warn("[FCM] no token returned");
        }
      }).catch((e) => {
        console.error("[FCM] token request failed:", e);
      });
    }
  }, []);

  const logout = useCallback(async () => {
    const deviceSession = localStorage.getItem("sf_device_session");

    try {
      if (deviceSession) {
        await fetch("/api/auth/logout", {
          method: "POST",
          headers: {
            "X-Device-Session": deviceSession,
          },
        });
      }
    } catch {}

    localStorage.removeItem("sf_token");
    localStorage.removeItem("sf_user");
    localStorage.removeItem("sf_role");
    localStorage.removeItem("sf_device_session");
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((u) => {
    localStorage.setItem("sf_user", JSON.stringify(u));
    setUser(u);
  }, []);

  const value = useMemo(
    () => ({
      token,
      user,
      role: user ? user.role || localStorage.getItem("sf_role") : null,
      login,
      logout,
      updateUser
    }),
    [token, user, login, logout, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
